import PolicyLayout from "@/components/PolicyLayout";

function Section({ title, children }) {
  return (
    <div className="mb-10">
      <h2 className="font-heading text-xl sm:text-2xl tracking-tight font-medium mb-4">{title}</h2>
      {children}
    </div>
  );
}

function P({ children }) {
  return <p className="text-base leading-relaxed text-muted-foreground mb-3">{children}</p>;
}

function List({ items }) {
  return (
    <ul className="space-y-2 mb-4">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-3 text-base leading-relaxed text-muted-foreground">
          <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--accent))] mt-2 flex-shrink-0" />
          {item}
        </li>
      ))}
    </ul>
  );
}

export default function PrivacyPage() {
  return (
    <PolicyLayout title="Privacy Policy" subtitle="Your data matters">
      <P>
        <em className="text-sm text-muted-foreground">Last updated: {new Date().toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}</em>
      </P>

      <Section title="1. What Data We Collect">
        <P>When you register for or interact with Cita Rush, we may collect:</P>
        <List items={[
          "Personal information: name, email address, phone number, gender, age or age band, city.",
          "Social profile link, if voluntarily submitted during registration.",
          "Event preferences: preferred venues, themes, age bands.",
          "Payment information processed through third-party payment gateways (we do not store card details directly).",
          "Communication history: emails, messages, and support conversations.",
          "Usage data: browser type, device information, and analytics data when you visit our website.",
        ]} />
      </Section>

      <Section title="2. Why We Collect Data">
        <P>Your data is collected and used for the following purposes:</P>
        <List items={[
          "Event registrations, waitlist management, and booking confirmations.",
          "Communicating event details, confirmations, and updates.",
          "Customer support and query resolution.",
          "Safety and security measures, including ID verification.",
          "Internal analytics to improve event quality and user experience.",
          "Marketing and promotional communications (only with your consent).",
        ]} />
      </Section>

      <Section title="3. How We Share Data">
        <P>
          We do not sell your personal data. Your information may be shared with:
        </P>
        <List items={[
          "Payment gateways for processing transactions securely.",
          "Email and communication service providers for sending confirmations and updates.",
          "Hosting and database infrastructure providers.",
          "Venue and event operations partners, limited to what is necessary for event management.",
        ]} />
        <P>
          <strong className="text-foreground">Important:</strong> Contact details of participants are never shared with
          other attendees unless there is a mutual match or explicit user consent. This is a core principle of Cita Rush.
        </P>
      </Section>

      <Section title="4. Your Rights">
        <List items={[
          "You can request access to the personal data we hold about you.",
          "You can request correction of inaccurate or incomplete data.",
          "You can request deletion of your data, subject to legal or operational retention requirements.",
          "You can withdraw consent for marketing communications at any time.",
        ]} />
      </Section>

      <Section title="5. Data Security">
        <P>
          We implement reasonable technical and organizational measures to protect your data against
          unauthorized access, alteration, disclosure, or destruction. However, no system is completely
          secure, and we cannot guarantee absolute security.
        </P>
      </Section>

      <Section title="6. Updates to This Policy">
        <P>
          Cita Rush may update this Privacy Policy from time to time. Any changes will be reflected on
          this page with an updated effective date. Continued use of our services after changes constitutes
          acceptance of the updated policy.
        </P>
      </Section>

      <Section title="7. Contact Us">
        <P>
          For privacy-related requests or questions, please contact us at{" "}
          <span className="text-foreground font-medium">privacy@citarush.com</span>.
        </P>
      </Section>
    </PolicyLayout>
  );
}
